import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import { NotFoundContent } from '@/pages/NotFound';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="pt-20">
            <NotFoundContent />
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.summary,
            image: article.imageUrl,
            author: {
              '@type': 'Person',
              name: article.author,
            },
            datePublished: article.publishDate,
            publisher: {
              '@type': 'Organization',
              name: '邪娠娼馆世界观指南',
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': `https://hnbocong.cn/article/${slug}`,
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'https://hnbocong.cn/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: category?.name || '分类',
                item: `https://hnbocong.cn/category/${article.category}`,
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: article.title,
              },
            ],
          }),
        }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                {
                  label: category?.name || '分类',
                  href: `/category/${article.category}`,
                },
                { label: article.title },
              ]}
            />

            <article className="py-8">
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-xs text-primary-400 bg-primary-500/10 px-3 py-1 rounded-full font-label"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-50 leading-tight mb-4">
                  {article.title}
                </h1>
                <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-user-line"></i>
                    {article.author}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-calendar-line"></i>
                    {article.publishDate}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-time-line"></i>
                    {article.readTime}
                  </span>
                </div>
              </div>

              <div className="aspect-[21/9] rounded-xl overflow-hidden mb-10">
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  title={`${article.title} - 邪娠娼馆世界观指南`}
                  className="w-full h-full object-cover"
                />
              </div>

              <div
                className="prose-custom text-foreground-200 leading-relaxed article-content"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="mt-12 pt-8 border-t border-background-300/40">
                <div className="flex flex-wrap gap-2">
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-xs text-foreground-500 bg-background-200 px-3 py-1.5 rounded-lg"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            </article>

            {relatedArticles.length > 0 && (
              <section className="py-12 border-t border-background-300/40">
                <h3 className="text-xl font-heading font-bold text-foreground-100 mb-6">
                  相关文章
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {relatedArticles.map((rel) => (
                    <Link
                      key={rel.slug}
                      to={`/article/${rel.slug}`}
                      className="group block p-5 rounded-xl bg-background-100 border border-background-300/30 hover:border-primary-500/30 transition-all duration-300 cursor-pointer"
                    >
                      <div className="aspect-[16/9] rounded-lg overflow-hidden mb-4">
                        <img
                          src={rel.imageUrl}
                          alt={rel.title}
                          title={`${rel.title} - 邪娠娼馆世界观指南`}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        />
                      </div>
                      <h4 className="text-sm font-heading font-semibold text-foreground-100 group-hover:text-primary-400 transition-colors line-clamp-2 mb-2">
                        {rel.title}
                      </h4>
                      <p className="text-xs text-foreground-500 line-clamp-2">
                        {rel.summary}
                      </p>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}